import * as React from "react";
import { buttonVariants } from "~/components/ui/button";
import { FOCUS_RING, cn } from "~/lib/utils";

/**
 * The only application route a public visitor can reach. Pricing, terms and
 * privacy are omitted until those pages and documents exist.
 */
const SIGN_IN_HREF = "/signin";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface PublicShellProps {
  children: React.ReactNode;
  className?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function PublicShell({ children, className }: PublicShellProps): React.ReactElement {
  return (
    <div
      data-slot="public-shell"
      className={cn("flex min-h-screen flex-col bg-surface-1", className)}
    >
      <a
        href="#main-content"
        className={cn(
          "absolute left-3 top-3 z-50 -translate-y-[calc(100%+1.5rem)] rounded-md bg-background px-3 py-2 text-sm font-medium text-foreground shadow-md",
          "focus:translate-y-0 focus:outline-none focus:shadow-[var(--shadow-focus)]",
          FOCUS_RING,
        )}
      >
        Skip to content
      </a>

      <header className="sticky top-0 z-40 border-b border-border bg-background">
        <div className="flex h-12 w-full items-center justify-between gap-3 px-4 sm:px-6">
          <a
            href="/"
            className={cn(
              "inline-flex min-h-10 items-center gap-2.5 rounded-md text-foreground",
              FOCUS_RING,
            )}
            aria-label="InboxFury home"
          >
            <span
              className="flex size-7 shrink-0 items-center justify-center rounded-md bg-foreground text-[11px] font-bold leading-none text-background"
              aria-hidden="true"
            >
              IF
            </span>
            <span className="text-sm font-semibold tracking-tight">InboxFury</span>
          </a>
          <a
            href={SIGN_IN_HREF}
            className={cn(
              buttonVariants({ size: "sm" }),
              "min-h-8 focus-visible:ring-0 focus-visible:ring-offset-0",
              FOCUS_RING,
            )}
          >
            Sign in
          </a>
        </div>
      </header>

      <main
        id="main-content"
        role="main"
        tabIndex={-1}
        className={cn(
          "flex flex-1 scroll-mt-12 items-center justify-center px-4 py-10 sm:py-16",
          FOCUS_RING,
        )}
      >
        <div
          className={cn(
            "w-full max-w-md",
            // Hairline divider anchoring the card header. shadcn's CardHeader
            // keys its `[.border-b]:pb-6` rule off a literal `border-b` class,
            // which slot targeting doesn't add — so we set the bottom padding
            // here explicitly alongside the rule.
            "[&_[data-slot=card-header]]:border-b [&_[data-slot=card-header]]:border-border [&_[data-slot=card-header]]:pb-6",
          )}
        >
          {children}
        </div>
      </main>
    </div>
  );
}
